import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TrashIcon, EyeIcon, CloudIcon, ArrowPathIcon, ViewColumnsIcon, QueueListIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import api from '../../services/api';

import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import ResourceSyncModal from '../../components/ResourceSyncModal';
import EngagementSelectionModal from '../../components/EngagementSelectionModal';
import ResourceTreeView from '../../components/ResourceTreeView';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import { getSyncRecencyStatus } from '../../utils/dateHelpers';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const cloudProviderOptions = [
  { value: 'Azure', label: 'Azure' },
  { value: 'AWS', label: 'AWS' },
  { value: 'GCP', label: 'GCP' },
];

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Deleted', label: 'Deleted' },
];

const syncStatusOptions = [
  { value: 'success', label: 'Synced' },
  { value: 'failed', label: 'Failed' },
  { value: 'manual', label: 'Manual' },
];

const UNASSIGNED_SUBSCRIPTION_VALUE = '__unassigned__';

interface Engagement {
  id: string;
  name: string;
  number: string;
}

interface EngagementSubscription {
  id: string;
  name: string | null;
  subscriptionKey: string;
  azureSubscriptionId?: string | null;
  awsAccountId?: string | null;
  gcpProjectId?: string | null;
  providerType?: string;
}

interface SubscriptionOption {
  id: string;
  name: string | null;
  subscriptionKey: string;
  azureSubscriptionId?: string | null;
  awsAccountId?: string | null;
  gcpProjectId?: string | null;
  providerType: string;
  engagementId: string;
  engagementName: string;
  engagementNumber: string;
}

function getSubscriptionIdentifier(
  providerType: string,
  subscription: Pick<
    SubscriptionOption,
    'subscriptionKey' | 'azureSubscriptionId' | 'awsAccountId' | 'gcpProjectId'
  >
): string {
  switch (providerType) {
    case 'AWS':
      return subscription.awsAccountId?.trim() || subscription.subscriptionKey;
    case 'GCP':
      return subscription.gcpProjectId?.trim() || subscription.subscriptionKey;
    case 'Azure':
    default:
      return subscription.azureSubscriptionId?.trim() || subscription.subscriptionKey;
  }
}

interface Resource {
  id: string;
  name: string;
  resourceGroupName: string;
  resourceType: string;
  resourceId: string;
  cloudProvider: string;
  region: string | null;
  status: string;
  syncedAt: string | null;
  lastSyncStatus: string | null;
  Engagement: Engagement;
  EngagementSubscription: EngagementSubscription | null;
  createdAt: string;
  updatedAt: string;
}

interface ResourceGroup {
  name: string;
  count: number;
  engagement: {
    id: string;
    name: string;
    number: string;
  };
  resources: Resource[];
}

const ResourceList: React.FC = () => {
  const { formatDateTime } = useDateFormatter();
  const [searchParams, setSearchParams] = useSearchParams();
  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [resources, setResources] = useState<Resource[]>([]);
  const [resourceGroups, setResourceGroups] = useState<ResourceGroup[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearch = searchParams.get('search') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterEngagement = useMemo(
    () => (searchParams.get('engagement') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterSubscriptionId = useMemo(
    () => (searchParams.get('subscriptionId') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterCloudProvider = searchParams.get('cloudProvider') ?? '';
  const filterStatus = searchParams.get('status') ?? '';
  const filterSyncStatus = searchParams.get('syncStatus') ?? '';
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [showResourceSyncModal, setShowResourceSyncModal] = useState(false);
  const [selectedEngagement, setSelectedEngagement] = useState<{ id: string; name: string } | null>(null);
  const [subscriptionOptions, setSubscriptionOptions] = useState<SubscriptionOption[]>([]);
  const [engagementOptions, setEngagementOptions] = useState<{ value: string; label: string }[]>([]);
  const navigate = useNavigate();
  const { canViewEntity, canCreateEntity, canUpdateEntity, loading: permissionsLoading } = useUserPermissions();

  const canAccessResources = canViewEntity('resources');
  const canCreateResources = canCreateEntity('resources');
  const canUpdateResources = canUpdateEntity('resources');

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [subscriptionsResponse, engagementsResponse] = await Promise.all([
          api.get('/subscriptions/dropdown'),
          api.get('/engagements/dropdown'),
        ]);
        setSubscriptionOptions(subscriptionsResponse.data.subscriptions ?? []);
        setEngagementOptions(
          (engagementsResponse.data.engagements ?? []).map(
            (e: { id: string; name: string; number: string }) => ({
              value: e.id,
              label: `${e.name} (${e.number})`,
            })
          )
        );
      } catch {
        setSubscriptionOptions([]);
        setEngagementOptions([]);
      }
    };
    if (canAccessResources) {
      fetchFilterOptions();
    }
  }, [canAccessResources]);

  const subscriptionFilterOptions = useMemo(() => {
    const options = subscriptionOptions.map((sub) => {
      const identifier = getSubscriptionIdentifier(sub.providerType, sub);
      return {
        value: sub.id,
        label: `${sub.name || identifier} (${sub.engagementNumber})`,
      };
    });
    return [
      { value: UNASSIGNED_SUBSCRIPTION_VALUE, label: 'Unassigned' },
      ...options,
    ];
  }, [subscriptionOptions]);

  const getSubscriptionLabel = useCallback(
    (subscriptionId: string) => {
      if (subscriptionId === UNASSIGNED_SUBSCRIPTION_VALUE) {
        return 'Unassigned';
      }
      const sub = subscriptionOptions.find((s) => s.id === subscriptionId);
      if (!sub) return subscriptionId;
      const identifier = getSubscriptionIdentifier(sub.providerType, sub);
      return `${sub.name || identifier} (${sub.engagementNumber})`;
    },
    [subscriptionOptions]
  );

  const getSubscriptionDisplayName = (
    subscription: EngagementSubscription | null,
    cloudProvider: string
  ) => {
    if (!subscription) return 'Unassigned';
    const providerType = subscription.providerType || cloudProvider;
    return (
      subscription.name ||
      getSubscriptionIdentifier(providerType, subscription)
    );
  };

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (appliedSearch) params.append('search', appliedSearch);
    if (filterName) params.append('name', filterName);
    if (filterEngagement.length > 0) params.append('engagementId', filterEngagement.join(','));
    if (filterSubscriptionId.length > 0) {
      const hasUnassigned = filterSubscriptionId.includes(UNASSIGNED_SUBSCRIPTION_VALUE);
      const ids = filterSubscriptionId.filter((id) => id !== UNASSIGNED_SUBSCRIPTION_VALUE);
      if (hasUnassigned) params.append('unassignedSubscription', 'true');
      if (ids.length > 0) params.append('subscriptionId', ids.join(','));
    }
    if (filterCloudProvider) params.append('cloudProvider', filterCloudProvider);
    if (filterStatus) params.append('status', filterStatus);
    if (filterSyncStatus) params.append('syncStatus', filterSyncStatus);
    return params;
  }, [appliedSearch, filterName, filterEngagement, filterSubscriptionId, filterCloudProvider, filterStatus, filterSyncStatus]);

  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      const params = buildFilterParams();
      params.append('limit', pageSize.toString());
      params.append('offset', offset.toString());

      const response = await api.get(`/resources?${params}`);
      setResources(response.data.resources);
      setTotal(response.data.total);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch resources';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pageSize, offset, buildFilterParams]);

  const fetchResourceTree = useCallback(async () => {
    try {
      setLoading(true);
      const params = buildFilterParams();
      const response = await api.get(`/resources/tree?${params}`);
      setResourceGroups(response.data.resourceGroups);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch resource tree';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  useEffect(() => {
    if (permissionsLoading) return;
    if (!canAccessResources) {
      navigate('/dashboard');
      return;
    }
    if (viewMode === 'table') {
      fetchResources();
    } else {
      fetchResourceTree();
    }
  }, [canAccessResources, navigate, viewMode, fetchResources, fetchResourceTree, permissionsLoading]);

  const handleDelete = async (id: string, resourceName: string) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete resource "${resourceName}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm'
        },
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await api.delete(`/resources/${id}`);
              toast.success('Resource deleted successfully');
              fetchResources();
            } catch (err: any) {
              const errorMessage = err.response?.data?.error || 'Failed to delete resource';
              toast.error(errorMessage);
            }
          },
          className: 'px-6 py-2.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm ml-2'
        }
      ],
      overlayClassName: 'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center'
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({ search: searchInput.trim() || null });
    setOffset(0);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value === '') {
      patchSearchParams({ search: null });
      setOffset(0);
    }
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'resource':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by resource..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'engagement':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={engagementOptions}
              value={filterEngagement}
              placeholder="All Engagements"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ engagement: arr.length > 0 ? arr.join(',') : null });
                setOffset(0);
              }}
            />
          );
        case 'subscription':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={subscriptionFilterOptions}
              value={filterSubscriptionId}
              placeholder="All Subscriptions"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ subscriptionId: arr.length > 0 ? arr.join(',') : null });
                setOffset(0);
              }}
            />
          );
        case 'provider':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={cloudProviderOptions}
              value={filterCloudProvider}
              placeholder="All Providers"
              onChange={(v) => {
                patchSearchParams({ cloudProvider: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={statusOptions}
              value={filterStatus}
              placeholder="All Statuses"
              onChange={(v) => {
                patchSearchParams({ status: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'syncStatus':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={syncStatusOptions}
              value={filterSyncStatus}
              placeholder="All Sync Statuses"
              onChange={(v) => {
                patchSearchParams({ syncStatus: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [filterName, filterEngagement, engagementOptions, filterSubscriptionId, subscriptionFilterOptions, filterCloudProvider, filterStatus, filterSyncStatus, patchSearchParams]
  );

  const getEngagementLabel = useCallback(
    (engagementId: string) => {
      const engagement = engagementOptions.find((e) => e.value === engagementId);
      return engagement?.label || engagementId;
    },
    [engagementOptions]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) {
      chips.push({ id: 'search', label: 'Search', value: `"${appliedSearch}"`, color: 'blue' });
    }
    if (filterName) {
      chips.push({ id: 'name', label: 'Resource', value: filterName, color: 'blue' });
    }
    if (filterEngagement.length > 0) {
      chips.push({
        id: 'engagement',
        label: 'Engagement',
        value: filterEngagement.map(getEngagementLabel).join(', '),
        color: 'blue',
      });
    }
    if (filterSubscriptionId.length > 0) {
      chips.push({
        id: 'subscriptionId',
        label: 'Subscription',
        value: filterSubscriptionId.map(getSubscriptionLabel).join(', '),
        color: 'blue',
      });
    }
    if (filterCloudProvider) {
      chips.push({ id: 'cloudProvider', label: 'Provider', value: filterCloudProvider, color: 'teal' });
    }
    if (filterStatus) {
      chips.push({ id: 'status', label: 'Status', value: filterStatus, color: 'purple' });
    }
    if (filterSyncStatus) {
      const syncLabel = syncStatusOptions.find((o) => o.value === filterSyncStatus)?.label || filterSyncStatus;
      chips.push({ id: 'syncStatus', label: 'Sync Status', value: syncLabel, color: 'orange' });
    }
    return chips;
  }, [appliedSearch, filterName, filterEngagement, getEngagementLabel, filterSubscriptionId, getSubscriptionLabel, filterCloudProvider, filterStatus, filterSyncStatus]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null });
        break;
      case 'name':
        patchSearchParams({ name: null });
        break;
      case 'engagement':
        patchSearchParams({ engagement: null });
        break;
      case 'subscriptionId':
        patchSearchParams({ subscriptionId: null });
        break;
      case 'cloudProvider':
        patchSearchParams({ cloudProvider: null });
        break;
      case 'status':
        patchSearchParams({ status: null });
        break;
      case 'syncStatus':
        patchSearchParams({ syncStatus: null });
        break;
    }
    setOffset(0);
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      engagement: null,
      subscriptionId: null,
      cloudProvider: null,
      status: null,
      syncStatus: null,
    });
    setOffset(0);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCloudProviderIcon = (_provider: string) => {
    return <CloudIcon className="h-4 w-4 inline mr-1" />;
  };

  const getSyncStatusBadge = (status: string | null, syncedAt: string | null) => {
    if (!status || !syncedAt) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Manual</span>;
    }
    if (status === 'success') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Synced</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Failed</span>;
  };

  const handleAddResource = () => {
    setShowEngagementModal(true);
  };

  const handleEngagementSelect = (engagement: { id: string; name: string }) => {
    setSelectedEngagement(engagement);
    setShowEngagementModal(false);
    setShowResourceSyncModal(true);
  };

  const handleResourcesSynced = (syncedResources: any[]) => {
    if (viewMode === 'table') {
      fetchResources();
    } else {
      fetchResourceTree();
    }
    toast.success(`Successfully added ${syncedResources.length} resources`);
  };

  const handleResourceSelect = (resource: Resource, _resourceGroup: ResourceGroup) => {
    navigate(`/resources/${resource.id}`);
  };

  if (permissionsLoading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (!canAccessResources) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-sm text-gray-700">
            You do not have permission to access resources.
          </p>
        </div>
      </div>
    );
  }

  if (loading && resources.length === 0 && resourceGroups.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-center mt-4">Loading resources...</p>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Resources</h1>
              <p className="text-sm text-gray-600">Manage cloud resources for your engagements</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search resources..."
                  className="px-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Search
                </button>
              </form>
              <div className="flex rounded-md shadow-sm">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`relative inline-flex items-center px-3 py-1.5 rounded-l-md border text-sm font-medium ${
                    viewMode === 'table'
                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ViewColumnsIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  Table
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('tree')}
                  className={`relative -ml-px inline-flex items-center px-3 py-1.5 rounded-r-md border text-sm font-medium ${
                    viewMode === 'tree'
                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <QueueListIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  Tree
                </button>
              </div>
              {canCreateResources && (
                <button
                  onClick={handleAddResource}
                  className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <CloudIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  Add Resource
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {error && (
        <div className="mb-2 bg-red-50 border border-red-200 rounded-md p-4 flex-shrink-0">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <div className="text-sm text-gray-500">
            {viewMode === 'table'
              ? `Showing ${resources.length} of ${total} resources`
              : `Showing ${resourceGroups.length} resource groups`}
          </div>
          <button
            onClick={() => (viewMode === 'table' ? fetchResources() : fetchResourceTree())}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowPathIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          {viewMode === 'tree' ? (
            <div className="border border-gray-200 rounded-lg p-4 m-4">
              <ResourceTreeView
                resourceGroups={resourceGroups}
                onResourceSelect={handleResourceSelect}
              />
            </div>
          ) : (
            <>
              {resources.length === 0 ? (
                <div className="text-center py-12">
                  <CloudIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No resources found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by syncing resources from your cloud engagements.
                  </p>
                  {canCreateResources && (
                    <div className="mt-6">
                      <button
                        onClick={handleAddResource}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <CloudIcon className="-ml-1 mr-2 h-5 w-5" />
                        Add Resource
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          {renderColumnHeader('resource', 'Resource')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          {renderColumnHeader('engagement', 'Engagement')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          {renderColumnHeader('subscription', 'Subscription')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          {renderColumnHeader('provider', 'Provider')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          {renderColumnHeader('status', 'Status')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          {renderColumnHeader('syncStatus', 'Sync Status')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          Last Synced
                        </th>
                        <th className="relative px-6 py-3 bg-gray-50">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {resources.map((resource) => (
                        <tr key={resource.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{resource.name}</div>
                              <div className="text-sm text-gray-500">{resource.resourceType}</div>
                              <div className="text-xs text-gray-400">RG: {resource.resourceGroupName}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{resource.Engagement.name}</div>
                              <div className="text-sm text-gray-500">{resource.Engagement.number}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {getSubscriptionDisplayName(resource.EngagementSubscription, resource.cloudProvider)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              {getCloudProviderIcon(resource.cloudProvider)}
                              {resource.cloudProvider}
                            </div>
                            {resource.region && (
                              <div className="text-xs text-gray-500">{resource.region}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(resource.status)}`}>
                              {resource.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getSyncStatusBadge(resource.lastSyncStatus, resource.syncedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              {(() => {
                                const syncStatus = getSyncRecencyStatus(resource.syncedAt);
                                return (
                                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full w-fit ${
                                    syncStatus.status === 'recent'
                                      ? 'bg-green-100 text-green-800'
                                      : syncStatus.status === 'earlier'
                                      ? syncStatus.color === 'yellow'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : syncStatus.color === 'orange'
                                        ? 'bg-orange-100 text-orange-800'
                                        : 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {syncStatus.label}
                                  </span>
                                );
                              })()}
                              <span className="text-sm text-gray-500">
                                {resource.syncedAt ? formatDateTime(resource.syncedAt) : 'Never'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/resources/${resource.id}`}
                                className="text-blue-600 hover:text-blue-900"
                                title="View resource details"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Link>
                              {canUpdateResources && (
                                <button
                                  onClick={() => handleDelete(resource.id, resource.name)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete resource"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {viewMode === 'table' && total > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setOffset(0);
                }}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {PAGE_SIZE_OPTIONS.map((size, index) => {
                  const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                  const isDisabled = previousSize >= total && size > total;
                  return (
                    <option key={size} value={size} disabled={isDisabled}>
                      {size}
                    </option>
                  );
                })}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            <div className="text-sm text-gray-700">
              Showing {Math.min(offset + 1, total)} to {Math.min(offset + pageSize, total)} of {total} entries
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset(0)}
                disabled={offset === 0}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                &laquo;
              </button>
              <button
                onClick={() => setOffset(Math.max(0, offset - pageSize))}
                disabled={offset === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              {(() => {
                const totalPages = Math.ceil(total / pageSize);
                const currentPage = Math.floor(offset / pageSize) + 1;
                const pages: (number | string)[] = [];

                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else if (currentPage <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (currentPage >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                }

                return pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <span key={`ellipsis-${index}`} className="px-2 py-1 text-sm text-gray-500">
                        ...
                      </span>
                    );
                  }
                  const pageNum = page as number;
                  const isActive = pageNum === currentPage;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setOffset((pageNum - 1) * pageSize)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        isActive ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                });
              })()}
              <button
                onClick={() => setOffset(offset + pageSize)}
                disabled={offset + pageSize >= total}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setOffset(Math.floor((total - 1) / pageSize) * pageSize)}
                disabled={offset + pageSize >= total}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>

      <EngagementSelectionModal
        isOpen={showEngagementModal}
        onClose={() => setShowEngagementModal(false)}
        onEngagementSelect={handleEngagementSelect}
      />

      {selectedEngagement && (
        <ResourceSyncModal
          isOpen={showResourceSyncModal}
          onClose={() => {
            setShowResourceSyncModal(false);
            setSelectedEngagement(null);
          }}
          engagementId={selectedEngagement.id}
          engagementName={selectedEngagement.name}
          onResourcesSynced={handleResourcesSynced}
        />
      )}
    </div>
  );
};

export default ResourceList;
